import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { LocationCoords } from '../../types/fare';
import { QUICK_ROUTE_PRESETS } from '../../constants/farePresets';
import { MapPin, Navigation, Ruler, Loader2, RotateCcw, ChevronDown } from 'lucide-react';

interface LocationInputPanelProps {
  onCalculate: (
    pickup: LocationCoords,
    destination: LocationCoords | null,
    manualDistance: number | null,
    pickupAddress: string,
    destinationAddress: string,
    isQuickRoute?: boolean
  ) => void;
  onPickupSelect: (location: LocationCoords, address: string) => void;
  onDestinationSelect: (location: LocationCoords, address: string) => void;
  onStartPinSelection: (type: 'pickup' | 'destination') => void;
  onReset: () => void;
  loading: boolean;
  pickupLocation: LocationCoords | null;
  destinationLocation: LocationCoords | null;
  pickupAddress: string;
  destinationAddress: string;
}

export default function LocationInputPanel({
  onCalculate,
  onPickupSelect,
  onDestinationSelect,
  onStartPinSelection,
  onReset,
  loading,
  pickupLocation,
  destinationLocation,
  pickupAddress,
  destinationAddress,
}: LocationInputPanelProps) {
  const [manualDistanceMode, setManualDistanceMode] = useState(false);
  const [manualDistance, setManualDistance] = useState('');
  const [showQuickRoutes, setShowQuickRoutes] = useState(false);
  
  const pickupAutocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const destinationAutocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const quickRoutesRef = useRef<HTMLDivElement | null>(null);

  const handlePickupPlaceChanged = useCallback(() => {
    const place = pickupAutocomplete.current?.getPlace();
    if (place?.geometry?.location) {
      const location: LocationCoords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      onPickupSelect(location, place.formatted_address || place.name || '');
    }
  }, [onPickupSelect]);

  const handleDestinationPlaceChanged = useCallback(() => {
    const place = destinationAutocomplete.current?.getPlace();
    if (place?.geometry?.location) {
      const location: LocationCoords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      onDestinationSelect(location, place.formatted_address || place.name || '');
    }
  }, [onDestinationSelect]);

  const handleCalculate = () => {
    if (!pickupLocation) {
      alert('Please select a pickup location');
      return;
    }

    if (manualDistanceMode) {
      const distance = parseFloat(manualDistance);
      if (isNaN(distance) || distance <= 0) {
        alert('Please enter a valid distance');
        return;
      }
      if (distance > 400) {
        alert('Maximum distance is 400 km');
        return;
      }
      onCalculate(pickupLocation, null, distance, pickupAddress, '');
    } else {
      if (!destinationLocation) {
        alert('Please select a destination');
        return;
      }
      onCalculate(pickupLocation, destinationLocation, null, pickupAddress, destinationAddress);
    }
  };

  const handleQuickRoute = (routeId: string) => {
    const preset = QUICK_ROUTE_PRESETS.find(r => r.id === routeId);
    if (preset) {
      // Reset previous results and polylines first
      onReset();
      
      const startName = preset.name.split(' → ')[0];
      const endName = preset.name.split(' → ')[1];
      
      // Use setTimeout to ensure reset completes before setting new data
      setTimeout(() => {
        // Set the locations
        onPickupSelect(preset.start, startName);
        onDestinationSelect(preset.end, endName);
        setManualDistanceMode(false);
        
        // Automatically trigger fare calculation (marked as quick route - FREE for all users)
        onCalculate(preset.start, preset.end, null, startName, endName, true);
      }, 50);
      
      // Close dropdown after selection
      setShowQuickRoutes(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickRoutesRef.current && !quickRoutesRef.current.contains(event.target as Node)) {
        setShowQuickRoutes(false);
      }
    };

    if (showQuickRoutes) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQuickRoutes]);

  const getCurrentLocation = () => {
    onReset(); // Reset before setting new location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: LocationCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          onPickupSelect(location, 'Current Location');
        },
        (error) => {
          alert('Unable to get current location: ' + error.message);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const handlePickupInputFocus = () => {
    onReset(); // Reset when user starts typing in pickup field
  };

  const handlePickupPinClick = () => {
    onReset(); // Reset before starting pin selection
    onStartPinSelection('pickup');
  };

  return (
    <div className="flex flex-col h-full md:h-auto">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-start justify-between flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Fare Validator</h1>
          <p className="text-sm text-gray-600">Calculate tuk-tuk and taxi fares in Sri Lanka</p>
        </div>
        <button
          onClick={onReset}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Reset all fields"
        >
          <RotateCcw size={20} />
        </button>
      </div>

      {/* Inputs */}
      <div className="flex-1 overflow-y-auto md:overflow-visible p-6 space-y-6">
        {/* Pickup Location */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
            Pickup Location
          </label>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Autocomplete
              onLoad={(autocomplete) => (pickupAutocomplete.current = autocomplete)}
              onPlaceChanged={handlePickupPlaceChanged}
              options={{
                componentRestrictions: { country: 'lk' },
              }}
            >
              <input
                type="text"
                placeholder="Search for a location..."
                defaultValue={pickupAddress}
                onFocus={handlePickupInputFocus}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              />
            </Autocomplete>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={getCurrentLocation}
              className="text-sm py-2 px-3 bg-white border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <Navigation size={16} className="inline mr-1" />
              Current Location
            </button>
            <button
              onClick={handlePickupPinClick}
              className="text-sm py-2 px-3 bg-white border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <MapPin size={16} className="inline mr-1" />
              Pick on Map
            </button>
          </div>
        </div>

        {/* Destination / Manual Distance Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setManualDistanceMode(false)}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
              !manualDistanceMode
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Pin to Pin
          </button>
          <button
            onClick={() => setManualDistanceMode(true)}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
              manualDistanceMode
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Manual Distance
          </button>
        </div>

        {/* Destination or Manual Distance Input */}
        {!manualDistanceMode ? (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              Destination
            </label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Autocomplete
                onLoad={(autocomplete) => (destinationAutocomplete.current = autocomplete)}
                onPlaceChanged={handleDestinationPlaceChanged}
                options={{
                  componentRestrictions: { country: 'lk' },
                }}
              >
                <input
                  type="text"
                  placeholder="Search for destination..."
                  defaultValue={destinationAddress}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                />
              </Autocomplete>
            </div>
            <button
              onClick={() => onStartPinSelection('destination')}
              className="w-full mt-2 text-sm py-2 px-3 bg-white border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <MapPin size={16} className="inline mr-1" />
              Pick on Map
            </button>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              Distance (km)
            </label>
            <div className="relative">
              <Ruler className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="number"
                placeholder="Enter distance in km..."
                value={manualDistance}
                onChange={(e) => setManualDistance(e.target.value)}
                min="1"
                max="400"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Maximum: 400 km</p>
          </div>
        )}

        {/* Calculate Button */}
        <button
          onClick={handleCalculate}
          disabled={loading}
          className="w-full bg-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Calculating...
            </>
          ) : (
            'Calculate Fare'
          )}
        </button>

        {/* Quick Routes - Dropdown on Desktop, Grid on Mobile */}
        <div className="relative" ref={quickRoutesRef}>
          {/* Desktop: Compact Dropdown Button */}
          <div className="hidden md:block">
            <button
              onClick={() => setShowQuickRoutes(!showQuickRoutes)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-left hover:bg-gray-100 transition-colors flex items-center justify-between group"
            >
              <span className="text-sm font-medium text-gray-700">Quick Routes</span>
              <ChevronDown 
                size={18} 
                className={`text-gray-500 transition-transform ${showQuickRoutes ? 'rotate-180' : ''}`} 
              />
            </button>

            {/* Dropdown Menu */}
            {showQuickRoutes && (
              <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                {QUICK_ROUTE_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handleQuickRoute(preset.id)}
                    className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-600 mt-1">{preset.distanceKm} km</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile: Original Grid Layout */}
          <div className="md:hidden">
            <label className="block text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Quick Routes
            </label>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ROUTE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handleQuickRoute(preset.id)}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-left hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  <div className="text-sm font-semibold text-gray-900">{preset.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{preset.distanceKm} km</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

